// JobsView.jsx
// ============================================================================
// The real Jobs pipeline — backed by the shared owner-scoped DB (/api/jobs), so
// a job created here shows up everywhere Aria does (estimator, code scan,
// experience loop). This REPLACES the old localStorage-only LeadsView: cards are
// real rows, moving a card moves the job's status in the backend, and opening a
// job shows its estimate / QC scan / photos and the lifecycle actions.
//
// Pipeline mirrors the backend status machine:
//   lead → estimate → approved → scheduled → in_progress → complete → invoiced → paid
// (plus cancelled). ownerId is the signed-in account for now (becomes the session
// id once credentials land — same trust path as the rest of the app).
// ============================================================================

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getApiBase, fetchWithTimeout } from './apiBase';

const API = getApiBase();

const STAGES = [
  { key: 'lead', label: 'New lead' },
  { key: 'estimate', label: 'Quoted' },
  { key: 'approved', label: 'Approved' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'complete', label: 'Completed' },
  { key: 'invoiced', label: 'Invoiced' },
  { key: 'paid', label: 'Paid' },
];
const ALL_STATUSES = [...STAGES.map((s) => s.key), 'cancelled'];
const STAGE_LABEL = Object.fromEntries([...STAGES.map((s) => [s.key, s.label]), ['cancelled', 'Cancelled']]);

const TRADES = [
  { key: 'building', label: 'Carpentry / building' },
  { key: 'junk_removal', label: 'Junk removal' },
  { key: 'electrical', label: 'Electrical' },
  { key: 'plumbing', label: 'Plumbing' },
  { key: 'hvac', label: 'HVAC' },
  { key: 'roofing', label: 'Roofing' },
  { key: 'concrete_masonry', label: 'Concrete / masonry' },
  { key: 'demolition', label: 'Demolition' },
  { key: '', label: 'Other / not sure' },
];
const TRADE_LABEL = Object.fromEntries(TRADES.map((t) => [t.key, t.label]));

// Lead sources — mirrors services/jobs/leadSourceTiers.js on the backend
// (which stamps source_tier + lead_type at creation). Tier 1 = best.
const SOURCES = [
  { key: '', label: 'Where did this lead come from?' },
  { key: 'repeat_customer', label: 'Repeat customer', commercial: false },
  { key: 'referral', label: 'Referral / word of mouth', commercial: false },
  { key: 'property_managers', label: 'Property manager / landlord', commercial: true },
  { key: 'estate_probate', label: 'Estate sale / probate', commercial: true },
  { key: 'realtors', label: 'Realtor / agent', commercial: true },
  { key: 'construction_gcs', label: 'Contractor / GC', commercial: true },
  { key: 'storage_facilities', label: 'Storage facility', commercial: true },
  { key: 'commercial_offices_retail', label: 'Office / retail business', commercial: true },
  { key: 'restoration_contractors', label: 'Restoration company', commercial: true },
  { key: 'google_business_profile', label: 'Google / Maps', commercial: false },
  { key: 'google_lsa', label: 'Google ads (LSA)', commercial: false },
  { key: 'website_form', label: 'Website / funnel form', commercial: false },
  { key: 'nextdoor', label: 'Nextdoor', commercial: false },
  { key: 'facebook', label: 'Facebook / Instagram', commercial: false },
  { key: 'thumbtack_aggregators', label: 'Thumbtack / Angi', commercial: false },
  { key: 'drive_by_yard_sign', label: 'Saw the truck / yard sign', commercial: false },
];
const SOURCE_LABEL = Object.fromEntries(SOURCES.map((s) => [s.key, s.label]));
const COMMERCIAL_SOURCES = new Set(SOURCES.filter((s) => s.commercial).map((s) => s.key));
// Tier colors — rgba fills read on both the night base and theme-light day.
const TIER_STYLE = {
  1: { bg: 'rgba(34,197,94,.16)',  fg: '#22c55e', label: 'T1' },
  2: { bg: 'rgba(6,182,212,.16)',  fg: '#06b6d4', label: 'T2' },
  3: { bg: 'rgba(245,158,11,.16)', fg: '#f59e0b', label: 'T3' },
  4: { bg: 'rgba(251,146,60,.16)', fg: '#fb923c', label: 'T4' },
  5: { bg: 'rgba(239,68,68,.16)',  fg: '#ef4444', label: 'T5' },
};

// Owner = the signed-in account on this device. Falls back to a stable per-device
// id so the pipeline is consistent before real company auth exists.
function resolveOwnerId() {
  try {
    const uid = localStorage.getItem('aria_user_id');
    if (uid && uid.trim()) return uid.trim();
    let oid = localStorage.getItem('howto_owner_id');
    if (!oid) { oid = 'owner-' + Math.random().toString(36).slice(2, 10); localStorage.setItem('howto_owner_id', oid); }
    return oid;
  } catch (_) { return 'owner-local'; }
}

const money = (n) => (n == null || n === '' || !Number.isFinite(Number(n)) ? null : '$' + Math.round(Number(n)).toLocaleString('en-US'));
const quoteText = (j) => {
  const lo = j.quote_low, hi = j.quote_high;
  if (lo != null && hi != null && lo !== hi) return `${money(lo)}–${money(hi).replace('$', '')}`;
  if (hi != null) return money(hi);
  if (lo != null) return money(lo);
  return null;
};

const STYLE = `
.jobs{padding:16px 24px 64px;color:var(--text);font-family:inherit}
.jobs__top{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;margin-bottom:16px}
.jobs__title{font-size:22px;font-weight:600}
.jobs__sub{font-size:13px;color:var(--text-muted);margin-top:3px}
.jobs__add{background:var(--accent);color:#06210f;border:0;border-radius:999px;padding:9px 18px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit}
.jobs__add:hover{filter:brightness(1.06)}
.jobs__metrics{display:grid;grid-template-columns:repeat(auto-fit,minmax(120px,1fr));gap:10px;margin-bottom:22px}
.jobs__metric{background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);padding:12px 14px}
.jobs__metric-l{font-size:12px;color:var(--text-muted)}
.jobs__metric-v{font-size:20px;font-weight:600;margin-top:4px}
.jobs__board{display:flex;gap:12px;overflow-x:auto;padding-bottom:10px}
.jobs__col{flex:0 0 212px;min-width:212px;background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);padding:10px;display:flex;flex-direction:column;gap:8px}
.jobs__col-head{display:flex;align-items:center;justify-content:space-between;padding:2px 4px 6px}
.jobs__col-name{font-size:13px;font-weight:600}
.jobs__col-meta{font-size:11px;color:var(--text-muted)}
.jobs__card{background:var(--input-bg);border:1px solid var(--border);border-radius:10px;padding:10px 11px;cursor:pointer;text-align:left;width:100%;font-family:inherit;color:var(--text)}
.jobs__card:hover{border-color:var(--accent)}
.jobs__card-name{font-size:14px;font-weight:600}
.jobs__card-type{font-size:12px;color:var(--text-muted);margin-top:2px}
.jobs__card-addr{font-size:11.5px;color:var(--text-muted);margin-top:4px}
.jobs__card-foot{display:flex;align-items:center;justify-content:space-between;margin-top:9px}
.jobs__card-quote{font-size:13.5px;font-weight:600;color:var(--accent)}
.jobs__card-quote--none{color:var(--text-muted);font-weight:400;font-size:12px}
.jobs__move{margin-top:8px;width:100%;font-size:11.5px;background:transparent;color:var(--text-muted);border:1px solid var(--border);border-radius:8px;padding:5px 6px;font-family:inherit;cursor:pointer}
.jobs__empty{font-size:12px;color:var(--text-muted);text-align:center;padding:14px 4px}
.jobs__backdrop{position:fixed;inset:0;background:rgba(8,8,12,0.30);backdrop-filter:blur(10px) saturate(115%);-webkit-backdrop-filter:blur(10px) saturate(115%);display:flex;align-items:flex-start;justify-content:center;padding:40px 16px;z-index:9000;overflow-y:auto}
.jobs__modal{background:var(--surface);border:1px solid var(--border);border-radius:16px;width:100%;max-width:480px;padding:20px 22px}
.jobs__modal h3{font-size:18px;font-weight:600;margin-bottom:14px}
.jobs__field{margin-bottom:12px}
.jobs__field label{display:block;font-size:12px;color:var(--text-muted);margin-bottom:4px}
.jobs__field input,.jobs__field select,.jobs__field textarea{width:100%;background:var(--input-bg);border:1px solid var(--border);color:var(--text);border-radius:9px;padding:9px 11px;font-size:14px;font-family:inherit;outline:none}
.jobs__field input:focus,.jobs__field select:focus,.jobs__field textarea:focus{border-color:var(--accent)}
.jobs__row2{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.jobs__actions{display:flex;align-items:center;justify-content:flex-end;gap:14px;margin-top:18px}
.jobs__save{background:var(--accent);color:#06210f;border:0;border-radius:999px;padding:9px 20px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit}
.jobs__save:disabled{opacity:.5;cursor:default}
.jobs__cancel{background:transparent;color:var(--text-muted);border:0;font-size:14px;cursor:pointer;font-family:inherit}
.jobs__pill{font-size:11px;padding:2px 9px;border-radius:999px;border:1px solid var(--border);color:var(--text-muted)}
.jobs__steps{margin:14px 0 4px;border-top:1px solid var(--border)}
.jobs__step{display:flex;align-items:center;gap:11px;padding:11px 2px;border-bottom:1px solid var(--border)}
.jobs__step-ic{width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:14px;flex-shrink:0;border:1px solid var(--border)}
.jobs__step-ic--done{background:rgba(34,197,94,.15);border-color:rgba(34,197,94,.4);color:#22c55e}
.jobs__step-ic--todo{color:var(--text-muted)}
.jobs__step-t{font-size:13.5px;font-weight:600}
.jobs__step-s{font-size:11.5px;color:var(--text-muted);margin-top:1px}
.jobs__step-btn{margin-left:auto;background:transparent;border:1px solid var(--border);color:var(--text);border-radius:8px;padding:5px 12px;font-size:12px;cursor:pointer;font-family:inherit;white-space:nowrap}
.jobs__step-btn:hover{border-color:var(--accent)}
.jobs__step-btn:disabled{opacity:.45;cursor:default}
.jobs__note{font-size:11.5px;color:var(--text-muted);margin-top:10px;line-height:1.5}
.jobs__loading{font-size:13px;color:var(--text-muted);padding:40px 0;text-align:center}
.jobs__err{font-size:12.5px;color:#f87171;background:rgba(248,113,113,.1);border:1px solid rgba(248,113,113,.3);border-radius:9px;padding:9px 12px;margin-bottom:14px}
.jobs__filters{display:flex;gap:6px;margin-bottom:14px}
.jobs__filter{background:transparent;border:1px solid var(--border);color:var(--text-muted);border-radius:999px;padding:6px 15px;font-size:12.5px;font-family:inherit;cursor:pointer}
.jobs__filter:hover{border-color:var(--accent)}
.jobs__filter--on{background:var(--accent);color:#06210f;border-color:var(--accent);font-weight:600}
.jobs__srcrow{display:flex;align-items:center;gap:6px;margin-top:6px;flex-wrap:wrap}
.jobs__tier{font-size:10px;font-weight:700;padding:1px 7px;border-radius:999px;letter-spacing:.03em}
.jobs__srcname{font-size:11px;color:var(--text-muted)}
.jobs__biz{font-size:10.5px;font-weight:600;padding:1px 7px;border-radius:999px;background:rgba(6,182,212,.14);color:#06b6d4}
`;

const BLANK = { name: '', phone: '', address: '', trade: 'building', description: '', source: '', company: '' };

export default function JobsView() {
  const ownerId = useMemo(resolveOwnerId, []);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null); // full job
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // all | commercial | residential
  const estFileRef = useRef(null);
  const scanFileRef = useRef(null);

  const loadJobs = useCallback(async () => {
    setErr('');
    try {
      const r = await fetchWithTimeout(`${API}/api/jobs?ownerId=${encodeURIComponent(ownerId)}`, { timeout: 15000 });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || 'load failed');
      setJobs(Array.isArray(d.jobs) ? d.jobs : []);
    } catch (e) {
      setErr('Could not reach the backend (' + e.message + '). Is the server on :3001 running?');
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  const metrics = useMemo(() => {
    const active = jobs.filter((j) => j.status !== 'paid' && j.status !== 'cancelled');
    const val = (j) => Number(j.quote_high ?? j.quote_low ?? 0) || 0;
    return {
      active: active.length,
      pipeline: active.reduce((s, j) => s + val(j), 0),
      booked: jobs.filter((j) => j.status === 'scheduled' || j.status === 'in_progress').reduce((s, j) => s + val(j), 0),
      won: jobs.filter((j) => j.status === 'paid').reduce((s, j) => s + val(j), 0),
    };
  }, [jobs]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const createJob = async () => {
    if (!form.name.trim() && !form.description.trim()) return;
    setSaving(true);
    try {
      const r = await fetchWithTimeout(`${API}/api/jobs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ownerId,
          customer: { name: form.name.trim() || '(no name)', phone: form.phone.trim() || undefined },
          address: form.address.trim() || undefined,
          trade: form.trade || undefined,
          description: form.description.trim() || undefined,
          source: form.source || undefined,
          company: form.company.trim() || undefined,
        }),
        timeout: 15000,
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || 'create failed');
      setAddOpen(false); setForm(BLANK);
      await loadJobs();
    } catch (e) { setErr('Could not create the job: ' + e.message); }
    finally { setSaving(false); }
  };

  const moveStatus = async (id, status) => {
    try {
      const r = await fetchWithTimeout(`${API}/api/jobs/${id}/status`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ownerId, status }), timeout: 15000,
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || 'move failed');
      setJobs((js) => js.map((j) => (j.id === id ? { ...j, status } : j)));
      setDetail((dt) => (dt && dt.job && dt.job.id === id ? { ...dt, job: { ...dt.job, status } } : dt));
    } catch (e) { setErr('Could not move the job: ' + e.message); }
  };

  const openDetail = async (id) => {
    setDetailLoading(true); setDetail({ job: { id } });
    try {
      const r = await fetchWithTimeout(`${API}/api/jobs/${id}?ownerId=${encodeURIComponent(ownerId)}`, { timeout: 15000 });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || 'not found');
      setDetail(d);
    } catch (e) { setErr('Could not open the job: ' + e.message); setDetail(null); }
    finally { setDetailLoading(false); }
  };
  const refreshDetail = async () => { if (detail && detail.job) await openDetail(detail.job.id); await loadJobs(); };

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const fr = new FileReader(); fr.onload = () => resolve(fr.result); fr.onerror = reject; fr.readAsDataURL(file);
  });

  // Estimate + QC scan call the vision model — that costs money per photo, so we
  // confirm before firing (at runtime the customer's job covers it).
  const onEstimateFile = async (e) => {
    const file = e.target.files && e.target.files[0]; e.target.value = '';
    if (!file || !detail?.job?.id) return;
    if (!window.confirm('Run an AI estimate on this photo? This uses the vision model (a small per-photo cost).')) return;
    setBusy('estimate');
    try {
      const dataUrl = await fileToDataUrl(file);
      const r = await fetchWithTimeout(`${API}/api/jobs/${detail.job.id}/estimate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ownerId, imageBase64: dataUrl }), timeout: 60000,
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || 'estimate failed');
      await refreshDetail();
    } catch (err2) { setErr('Estimate failed: ' + err2.message); }
    finally { setBusy(''); }
  };
  const onScanFile = async (e) => {
    const file = e.target.files && e.target.files[0]; e.target.value = '';
    if (!file || !detail?.job?.id) return;
    if (!window.confirm('Run a QC / code scan on this photo? This uses the vision model (a small per-photo cost).')) return;
    setBusy('scan');
    try {
      const dataUrl = await fileToDataUrl(file);
      const r = await fetchWithTimeout(`${API}/api/jobs/${detail.job.id}/scan`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ownerId, imageBase64: dataUrl }), timeout: 60000,
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || 'scan failed');
      await refreshDetail();
    } catch (err2) { setErr('Scan failed: ' + err2.message); }
    finally { setBusy(''); }
  };

  const completeJob = async () => {
    if (!detail?.job?.id) return;
    const charged = window.prompt('What did you charge for this job? (final $ — folds into Aria’s learned pricing)');
    if (charged == null) return;
    setBusy('complete');
    try {
      const r = await fetchWithTimeout(`${API}/api/jobs/${detail.job.id}/complete`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ownerId, actuals: { charged: Number(charged) || 0, trade: detail.job.trade } }), timeout: 15000,
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || 'complete failed');
      await refreshDetail();
    } catch (err2) { setErr('Could not complete the job: ' + err2.message); }
    finally { setBusy(''); }
  };

  const renderDetail = () => {
    if (!detail) return null;
    const j = detail.job || {};
    const cust = detail.customer;
    const addr = detail.property?.address || j.address;
    const est = (detail.estimates && detail.estimates[0]) || null;
    const photos = detail.photos || [];
    const scans = detail.codeScans || [];
    const has = (kind) => photos.some((p) => p.kind === kind) || (kind === 'estimate' && !!est) || (kind === 'codescan' && scans.length > 0);
    const stepIc = (done, icon) => (
      <span className={`jobs__step-ic ${done ? 'jobs__step-ic--done' : 'jobs__step-ic--todo'}`}>{done ? '✓' : icon}</span>
    );
    return (
      <div className="jobs__backdrop" onClick={() => setDetail(null)}>
        <div className="jobs__modal" onClick={(e) => e.stopPropagation()}>
          {detailLoading ? <div className="jobs__loading">Loading…</div> : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div>
                  <h3 style={{ marginBottom: 4 }}>{cust?.name || j.title || `Job #${j.id}`}</h3>
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                    {TRADE_LABEL[j.trade] || j.trade || 'Trade not set'}{j.description ? ` · ${j.description}` : ''}
                  </div>
                  {(j.source || j.lead_type === 'commercial') && (
                    <div className="jobs__srcrow">
                      {j.source_tier && TIER_STYLE[j.source_tier] && (
                        <span className="jobs__tier" style={{ background: TIER_STYLE[j.source_tier].bg, color: TIER_STYLE[j.source_tier].fg }}>
                          Tier {j.source_tier}
                        </span>
                      )}
                      {j.source && <span className="jobs__srcname">via {SOURCE_LABEL[j.source] || j.source.replace(/_/g, ' ')}</span>}
                      {j.lead_type === 'commercial' && <span className="jobs__biz">{j.company || 'Commercial'}</span>}
                    </div>
                  )}
                  {addr && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>📍 {addr}</div>}
                  {cust?.phone && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{cust.phone}</div>}
                </div>
                <select className="jobs__pill" style={{ background: 'var(--input-bg)' }} value={j.status} onChange={(e) => moveStatus(j.id, e.target.value)}>
                  {ALL_STATUSES.map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
                </select>
              </div>

              <div className="jobs__steps">
                <div className="jobs__step">
                  {stepIc(has('estimate') || photos.some((p) => p.kind === 'estimate'), '📷')}
                  <div><div className="jobs__step-t">Estimate</div><div className="jobs__step-s">{est ? `${quoteText({ quote_low: est.quote_low, quote_high: est.quote_high }) || 'quoted'} · ${est.status || 'draft'}` : 'photo of the job → AI quote'}</div></div>
                  <button className="jobs__step-btn" disabled={busy === 'estimate'} onClick={() => estFileRef.current && estFileRef.current.click()}>{busy === 'estimate' ? 'Working…' : (est ? 'Re-estimate' : 'From photo')}</button>
                </div>
                <div className="jobs__step">
                  {stepIc(scans.length > 0, '🛡')}
                  <div><div className="jobs__step-t">QC / code scan</div><div className="jobs__step-s">{scans.length ? `${scans.length} scan${scans.length > 1 ? 's' : ''} · ${scans[0].domain || ''}` : 'after-photo → quality check'}</div></div>
                  <button className="jobs__step-btn" disabled={busy === 'scan'} onClick={() => scanFileRef.current && scanFileRef.current.click()}>{busy === 'scan' ? 'Working…' : 'From photo'}</button>
                </div>
                <div className="jobs__step">
                  {stepIc(j.status === 'complete' || j.status === 'invoiced' || j.status === 'paid', '🏁')}
                  <div><div className="jobs__step-t">Complete</div><div className="jobs__step-s">final $ folds into learned pricing</div></div>
                  <button className="jobs__step-btn" disabled={busy === 'complete'} onClick={completeJob}>{busy === 'complete' ? 'Working…' : 'Close out'}</button>
                </div>
                <div className="jobs__step">
                  <span className="jobs__step-ic jobs__step-ic--todo">✍</span>
                  <div><div className="jobs__step-t">Customer signature</div><div className="jobs__step-s">coming next — sent as a secure link</div></div>
                  <button className="jobs__step-btn" disabled>Soon</button>
                </div>
                <div className="jobs__step" style={{ borderBottom: 'none' }}>
                  <span className="jobs__step-ic jobs__step-ic--todo">💳</span>
                  <div><div className="jobs__step-t">Invoice & pay</div><div className="jobs__step-s">coming next — Stripe via the Operator</div></div>
                  <button className="jobs__step-btn" disabled>Soon</button>
                </div>
              </div>

              {photos.length > 0 && (
                <div className="jobs__note">Photos on file: {photos.map((p) => p.kind).join(', ')}.</div>
              )}
              <div className="jobs__note">Signature and in-app payment are the next two pieces — the customer gets a secure link for those, no app login.</div>

              <input ref={estFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onEstimateFile} />
              <input ref={scanFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onScanFile} />

              <div className="jobs__actions">
                <button className="jobs__cancel" onClick={() => setDetail(null)}>Close</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="jobs">
      <style>{STYLE}</style>

      <div className="jobs__top">
        <div>
          <div className="jobs__title">Jobs</div>
          <div className="jobs__sub">Every job from first call to paid — live in the system Aria learns from.</div>
        </div>
        <button className="jobs__add" type="button" onClick={() => { setForm(BLANK); setAddOpen(true); }}>+ New job</button>
      </div>

      {err && <div className="jobs__err">{err}</div>}

      <div className="jobs__filters">
        {[['all', 'All leads'], ['commercial', 'Commercial'], ['residential', 'Residential']].map(([k, label]) => (
          <button key={k} type="button" className={`jobs__filter ${typeFilter === k ? 'jobs__filter--on' : ''}`} onClick={() => setTypeFilter(k)}>{label}</button>
        ))}
      </div>

      <div className="jobs__metrics">
        <div className="jobs__metric"><div className="jobs__metric-l">Active jobs</div><div className="jobs__metric-v">{metrics.active}</div></div>
        <div className="jobs__metric"><div className="jobs__metric-l">Pipeline value</div><div className="jobs__metric-v">{money(metrics.pipeline) || '$0'}</div></div>
        <div className="jobs__metric"><div className="jobs__metric-l">Booked</div><div className="jobs__metric-v">{money(metrics.booked) || '$0'}</div></div>
        <div className="jobs__metric"><div className="jobs__metric-l">Collected</div><div className="jobs__metric-v" style={{ color: 'var(--accent)' }}>{money(metrics.won) || '$0'}</div></div>
      </div>

      {loading ? (
        <div className="jobs__loading">Loading jobs…</div>
      ) : (
        <div className="jobs__board">
          {STAGES.map((stage) => {
            const typeOk = (j) => typeFilter === 'all'
              || (typeFilter === 'commercial' ? j.lead_type === 'commercial' : j.lead_type !== 'commercial');
            const inStage = jobs.filter((j) => j.status === stage.key && typeOk(j));
            const sum = inStage.reduce((s, j) => s + (Number(j.quote_high ?? j.quote_low ?? 0) || 0), 0);
            return (
              <div className="jobs__col" key={stage.key}>
                <div className="jobs__col-head">
                  <span className="jobs__col-name">{stage.label}</span>
                  <span className="jobs__col-meta">{inStage.length}{sum ? ' · ' + money(sum) : ''}</span>
                </div>
                {inStage.length === 0 && <div className="jobs__empty">—</div>}
                {inStage.map((j) => {
                  const q = quoteText(j);
                  return (
                    <div className="jobs__card" key={j.id} role="button" tabIndex={0} onClick={() => openDetail(j.id)} onKeyDown={(e) => { if (e.key === 'Enter') openDetail(j.id); }}>
                      <div className="jobs__card-name">{j.customer_name || j.title || `Job #${j.id}`}</div>
                      <div className="jobs__card-type">{TRADE_LABEL[j.trade] || j.trade || 'Trade not set'}</div>
                      {(j.source || j.lead_type === 'commercial') && (
                        <div className="jobs__srcrow">
                          {j.source_tier && TIER_STYLE[j.source_tier] && (
                            <span className="jobs__tier" style={{ background: TIER_STYLE[j.source_tier].bg, color: TIER_STYLE[j.source_tier].fg }}>
                              {TIER_STYLE[j.source_tier].label}
                            </span>
                          )}
                          {j.source && <span className="jobs__srcname">{SOURCE_LABEL[j.source] || j.source.replace(/_/g, ' ')}</span>}
                          {j.lead_type === 'commercial' && <span className="jobs__biz">{j.company || 'Commercial'}</span>}
                        </div>
                      )}
                      {j.address && <div className="jobs__card-addr">📍 {j.address}</div>}
                      <div className="jobs__card-foot">
                        <span className={q ? 'jobs__card-quote' : 'jobs__card-quote jobs__card-quote--none'}>{q || (stage.key === 'lead' ? 'needs quote' : '—')}</span>
                      </div>
                      <select
                        className="jobs__move"
                        value={j.status}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => { e.stopPropagation(); moveStatus(j.id, e.target.value); }}
                      >
                        {ALL_STATUSES.map((s) => <option key={s} value={s}>Move to: {STAGE_LABEL[s]}</option>)}
                      </select>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {!loading && jobs.length === 0 && (
        <div className="jobs__empty" style={{ padding: '24px 0' }}>No jobs yet — hit “+ New job” to add your first one.</div>
      )}

      {addOpen && (
        <div className="jobs__backdrop" onClick={() => setAddOpen(false)}>
          <div className="jobs__modal" onClick={(e) => e.stopPropagation()}>
            <h3>New job</h3>
            <div className="jobs__field"><label>Customer name</label><input value={form.name} autoFocus onChange={(e) => set('name', e.target.value)} placeholder="e.g. Dave Thompson" /></div>
            <div className="jobs__row2">
              <div className="jobs__field"><label>Phone</label><input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(555) 000-0000" /></div>
              <div className="jobs__field"><label>Trade</label><select value={form.trade} onChange={(e) => set('trade', e.target.value)}>{TRADES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</select></div>
            </div>
            <div className="jobs__field"><label>Lead source</label>
              <select value={form.source} onChange={(e) => set('source', e.target.value)}>
                {SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            {COMMERCIAL_SOURCES.has(form.source) && (
              <div className="jobs__field"><label>Company / account name</label><input value={form.company} onChange={(e) => set('company', e.target.value)} placeholder="e.g. Keyrenter Property Management" /></div>
            )}
            <div className="jobs__field"><label>Job site address</label><input value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="412 Maple Ave" /></div>
            <div className="jobs__field"><label>What needs done</label><textarea rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Describe the job — Aria uses this for the estimate." /></div>
            <div className="jobs__actions">
              <button className="jobs__cancel" type="button" onClick={() => setAddOpen(false)}>Cancel</button>
              <button className="jobs__save" type="button" disabled={saving} onClick={createJob}>{saving ? 'Adding…' : 'Add job'}</button>
            </div>
          </div>
        </div>
      )}

      {renderDetail()}
    </div>
  );
}
